
import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import ChatMessage from "./ChatMessage";
import DiscordLikeInput from "./DiscordLikeInput";
import { useAuth } from "@/contexts/AuthContext";

type ChatMessage = Database['public']['Tables']['chat_messages']['Row'];

interface ThreadViewProps {
  parentMessage: ChatMessage;
  isOpen: boolean;
  onClose: () => void;
  getUserDisplayName: (userId: string) => string;
  onEdit?: (messageId: string, newContent: string) => Promise<boolean>;
  onDelete?: (messageId: string) => Promise<boolean>;
  onAddSticker?: (messageId: string, emoji: string) => Promise<void>;
  onRemoveSticker?: (messageId: string, emoji: string) => Promise<void>;
  messageStickers?: Map<string, Array<{
    id: string;
    emoji: string;
    userId: string;
    userName: string;
    count: number;
  }>>;
  canDeleteOthersMessages?: boolean;
}

export default function ThreadView({ 
  parentMessage, 
  isOpen, 
  onClose, 
  getUserDisplayName,
  onEdit,
  onDelete,
  onAddSticker,
  onRemoveSticker,
  messageStickers = new Map(),
  canDeleteOthersMessages = false
}: ThreadViewProps) {
  const { user } = useAuth();
  const [replies, setReplies] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && parentMessage.id) {
      fetchReplies();
    }
  }, [isOpen, parentMessage.id]);

  const fetchReplies = async () => {
    setLoading(true);
    try {
      console.log("ThreadView: Fetching replies for parent message:", parentMessage.id);
      
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("parent_message_id", parentMessage.id)
        .order("sent_at", { ascending: true });

      if (error) {
        console.error("ThreadView: Error fetching replies:", error);
        return;
      }

      console.log("ThreadView: Fetched replies:", data?.length || 0, "replies");
      setReplies(data || []);
    } catch (err) {
      console.error("ThreadView: Error fetching replies:", err);
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (content: string, mediaData?: {
    mediaUrl: string;
    mediaType: 'image' | 'video';
    mediaFilename: string;
    mediaSize: number;
  }) => {
    if (!user || !parentMessage.room_id) return;

    try {
      console.log("ThreadView: Sending reply to parent message:", parentMessage.id);
      
      const messageData = {
        room_id: parentMessage.room_id,
        sender_id: user.id,
        content: content,
        parent_message_id: parentMessage.id,
        ...(mediaData && {
          media_type: mediaData.mediaType,
          media_url: mediaData.mediaUrl,
          media_filename: mediaData.mediaFilename,
          media_size: mediaData.mediaSize
        })
      };

      console.log("ThreadView: Inserting reply with data:", messageData);

      const { error } = await supabase
        .from("chat_messages")
        .insert(messageData);

      if (error) {
        console.error("ThreadView: Error sending reply:", error);
        throw error;
      }

      console.log("ThreadView: Reply sent successfully");
      // Note: The real-time subscription will automatically add the new reply to the UI
    } catch (err) {
      console.error("ThreadView: Error sending reply:", err);
      throw err;
    }
  };

  // Real-time subscription for thread replies
  useEffect(() => {
    if (!isOpen || !parentMessage.id) return;

    console.log(`ThreadView: Setting up real-time subscription for thread: ${parentMessage.id}`);
    console.log(`ThreadView: Subscription filter will be: parent_message_id=eq.${parentMessage.id}`);

    const channel = supabase
      .channel(`thread-${parentMessage.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `parent_message_id=eq.${parentMessage.id}`
        },
        (payload) => {
          const newReply = payload.new as ChatMessage;
          console.log("ThreadView: Real-time INSERT event fired!");
          console.log("ThreadView: New reply received:", newReply);
          console.log("ThreadView: Reply parent_message_id:", newReply.parent_message_id);
          console.log("ThreadView: Expected parent_message_id:", parentMessage.id);
          
          // Add the new reply to the state, avoiding duplicates
          setReplies(prev => {
            console.log("ThreadView: Current replies count before adding:", prev.length);
            
            const exists = prev.some(reply => reply.id === newReply.id);
            if (exists) {
              console.log("ThreadView: Reply already exists, skipping duplicate");
              return prev;
            }
            
            // Insert in correct chronological order
            const updatedReplies = [...prev, newReply].sort((a, b) => 
              new Date(a.sent_at || '').getTime() - new Date(b.sent_at || '').getTime()
            );
            
            console.log("ThreadView: Added new reply to thread, total replies:", updatedReplies.length);
            console.log("ThreadView: New replies state:", updatedReplies.map(r => ({ id: r.id, content: r.content })));
            
            return updatedReplies;
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `parent_message_id=eq.${parentMessage.id}`
        },
        (payload) => {
          const updatedReply = payload.new as ChatMessage;
          console.log("ThreadView: Thread reply updated:", updatedReply);
          
          setReplies(prev => prev.map(reply => 
            reply.id === updatedReply.id ? updatedReply : reply
          ));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'chat_messages',
          filter: `parent_message_id=eq.${parentMessage.id}`
        },
        (payload) => {
          const deletedReply = payload.old as ChatMessage;
          console.log("ThreadView: Thread reply deleted:", deletedReply);
          
          setReplies(prev => prev.filter(reply => reply.id !== deletedReply.id));
        }
      )
      .subscribe((status) => {
        console.log(`ThreadView: Thread subscription status: ${status}`);
        if (status === 'SUBSCRIBED') {
          console.log("ThreadView: Successfully subscribed to thread real-time updates");
        } else if (status === 'CHANNEL_ERROR') {
          console.error("ThreadView: Channel error in thread subscription");
        } else if (status === 'TIMED_OUT') {
          console.error("ThreadView: Thread subscription timed out");
        }
      });

    return () => {
      console.log(`ThreadView: Cleaning up thread subscription for: ${parentMessage.id}`);
      supabase.removeChannel(channel);
    };
  }, [isOpen, parentMessage.id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Blurred background overlay */}
      <div 
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Thread view container */}
      <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl h-[80vh] mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-slate-700">
          <h3 className="text-lg font-semibold dark:text-white">Thread</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="dark:text-slate-300 dark:hover:bg-slate-700">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Parent message with full functionality */}
        <div className="p-4 border-b dark:border-slate-700 bg-muted/30 dark:bg-slate-700/30">
          <ChatMessage
            message={parentMessage}
            isCurrentUser={parentMessage.sender_id === user?.id}
            senderName={parentMessage.sender_id ? getUserDisplayName(parentMessage.sender_id) : undefined}
            onEdit={onEdit}
            onDelete={onDelete}
            onReply={undefined} // Don't allow reply to thread parent (prevents infinite nesting)
            onAddSticker={onAddSticker}
            onRemoveSticker={onRemoveSticker}
            messageStickers={messageStickers.get(parentMessage.id) || []}
            getUserDisplayName={getUserDisplayName}
            isReply={false}
            canDeleteOthersMessages={canDeleteOthersMessages}
          />
        </div>

        {/* Replies */}
        <ScrollArea className="flex-1 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-anyskill-purple"></div>
              <p className="text-muted-foreground dark:text-slate-400 ml-3">Loading replies...</p>
            </div>
          ) : replies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground dark:text-slate-400">
              No replies yet. Be the first to reply!
            </div>
          ) : (
            <div className="space-y-4">
              {replies.map((reply) => (
                <ChatMessage
                  key={reply.id}
                  message={reply}
                  isCurrentUser={reply.sender_id === user?.id}
                  senderName={reply.sender_id ? getUserDisplayName(reply.sender_id) : undefined}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onReply={undefined} // Don't allow nested replies in threads
                  onAddSticker={onAddSticker}
                  onRemoveSticker={onRemoveSticker}
                  messageStickers={messageStickers.get(reply.id) || []}
                  getUserDisplayName={getUserDisplayName}
                  isReply={true}
                  canDeleteOthersMessages={canDeleteOthersMessages}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Reply input */}
        <div className="border-t dark:border-slate-700">
          <DiscordLikeInput 
            onSendMessage={sendReply}
            placeholder="Reply to thread..."
          />
        </div>
      </div>
    </div>
  );
}
